import React, { useState } from 'react';
import type { User, Role, InviteUserDto } from '../types';

interface Props {
  users: User[];
  roles: Role[];
  onInviteUser: (user: InviteUserDto) => void;
  onUpdateUser: (payload: UpdateUserPayload) => void;
  onDeleteUser: (userIdx: number) => void;
}

export interface UpdateUserPayload {
  userIdx: number;
  roleIdx?: number;
  status?: string;
  name?: string;
}

export default function UsersTab({ users, roles, onInviteUser, onUpdateUser, onDeleteUser }: Props) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [inviteFormData, setInviteFormData] = useState<InviteUserDto>({
    name: '', loginId: '', phoneNumber: '', email: '', roleIdx: 0, status: 'PENDING'
  });

  const [loginIdChecked, setLoginIdChecked] = useState<null | boolean>(null);
  const [isCheckingLoginId, setIsCheckingLoginId] = useState(false);

  const handleCheckLoginId = async () => {
    const id = inviteFormData.loginId.trim();
    if (!id) { alert('로그인 ID를 입력해주세요.'); return; }
    setIsCheckingLoginId(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`https://api.wedmin.biz/user/check-login-id?loginId=${encodeURIComponent(id)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await res.json();
      const available = result?.data?.available ?? result?.available ?? false;
      setLoginIdChecked(available);
      if (available) alert('사용 가능한 아이디입니다.');
      else alert('이미 사용 중인 아이디입니다.');
    } catch { alert('중복 확인 중 오류가 발생했습니다.'); }
    finally { setIsCheckingLoginId(false); }
  };

  const [manageFormData, setManageFormData] = useState({
    roleIdx: 0,
    status: ''
  });

  const openInviteModal = () => {
    setInviteFormData({ name: '', loginId: '', phoneNumber: '', email: '', roleIdx: roles[0]?.roleIdx || 0, status: 'PENDING' });
    setLoginIdChecked(null);
    setShowInviteModal(true);
  };

  const openManageModal = (user: User) => {
    setSelectedUser(user);
    setManageFormData({
      roleIdx: user.roleIdx || 0,
      status: user.status
    });
    setShowManageModal(true);
  };

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...inviteFormData };
    if (!payload.email?.trim()) delete (payload as any).email;
    onInviteUser(payload);
    setShowInviteModal(false);
  };

  const handleManageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    onUpdateUser({
      userIdx: selectedUser.userIdx,
      roleIdx: Number(manageFormData.roleIdx),
      status: manageFormData.status
    });
    setShowManageModal(false);
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button
          onClick={openInviteModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-user-add-line mr-2"></i> 사용자 초대
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">이름</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">아이디</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">연락처</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">이메일</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">역할</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">상태</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">마지막 로그인</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.userIdx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.loginId || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{user.phoneNumber}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {user.roleName || (user.role && user.role.name) || '-'}
                      </span>
                      {(user.isAdmin || user.roleType === 'ADMIN') && (
                        <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                          ADMIN
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{user.lastLoginDt || '-'}</td>
                  <td className="px-6 py-4 flex gap-2">
                    <button
                      onClick={() => openManageModal(user)}
                      className="text-blue-600 hover:text-blue-800 w-8 h-8 flex items-center justify-center rounded hover:bg-blue-50"
                      title="정보 수정"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => onDeleteUser(user.userIdx)}
                      className="text-red-600 hover:text-red-800 w-8 h-8 flex items-center justify-center rounded hover:bg-red-50"
                      title="삭제"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 사용자 초대 모달 */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">사용자 초대</h2>
              <button onClick={() => setShowInviteModal(false)}><i className="ri-close-line text-2xl"></i></button>
            </div>
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                <input type="text" value={inviteFormData.name} onChange={e => setInviteFormData({...inviteFormData, name: e.target.value})} className="w-full border rounded-md px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">로그인 ID *</label>
                <div className="flex gap-2">
                  <input type="text" value={inviteFormData.loginId} onChange={e => { setInviteFormData({...inviteFormData, loginId: e.target.value}); setLoginIdChecked(null); }} className="flex-1 border rounded-md px-3 py-2" placeholder="예: 한글업체명_staff01" required />
                  <button type="button" onClick={handleCheckLoginId} disabled={isCheckingLoginId}
                    className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                      loginIdChecked === true ? 'bg-green-100 text-green-700' : loginIdChecked === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                    {isCheckingLoginId ? '확인중...' : loginIdChecked === true ? '사용가능' : loginIdChecked === false ? '사용불가' : '중복확인'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                <input type="tel" value={inviteFormData.phoneNumber} onChange={e => setInviteFormData({...inviteFormData, phoneNumber: e.target.value})} className="w-full border rounded-md px-3 py-2" placeholder="010-0000-0000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                <input type="email" value={inviteFormData.email} onChange={e => setInviteFormData({...inviteFormData, email: e.target.value})} className="w-full border rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">역할</label>
                <select value={inviteFormData.roleIdx} onChange={e => setInviteFormData({...inviteFormData, roleIdx: Number(e.target.value)})} className="w-full border rounded-md px-3 py-2">
                  <option value={0}>선택하세요</option>
                  {roles.map(role => (
                    <option key={role.roleIdx} value={role.roleIdx}>
                      {role.name} {role.roleType === 'ADMIN' ? '(ADMIN)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowInviteModal(false)} className="px-4 py-2 bg-gray-200 rounded-md">취소</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md">초대하기</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 사용자 수정 모달 */}
      {showManageModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">사용자 관리</h2>
              <button onClick={() => setShowManageModal(false)}><i className="ri-close-line text-2xl"></i></button>
            </div>
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-semibold text-lg">{selectedUser.name}</span>
                <span className="text-sm text-gray-500">({selectedUser.status})</span>
              </div>
              {selectedUser.loginId && <p className="text-sm text-gray-600">아이디: {selectedUser.loginId}</p>}
              <p className="text-sm text-gray-600">{selectedUser.email}</p>
              <p className="text-sm text-gray-600">{selectedUser.phoneNumber}</p>
            </div>
            <form onSubmit={handleManageSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">역할 변경</label>
                <select
                  value={manageFormData.roleIdx}
                  onChange={e => setManageFormData({...manageFormData, roleIdx: Number(e.target.value)})}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value={0}>역할 선택</option>
                  {roles.map(role => (
                    <option key={role.roleIdx} value={role.roleIdx}>
                      {role.name} {role.roleType === 'ADMIN' ? '(ADMIN)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태 변경</label>
                <select
                  value={manageFormData.status}
                  onChange={e => setManageFormData({...manageFormData, status: e.target.value})}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="PENDING">대기중 (PENDING)</option>
                  <option value="ACTIVE">활성 (ACTIVE)</option>
                  <option value="BLOCKED">차단 (BLOCKED)</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
                <button type="button" onClick={() => setShowManageModal(false)} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">취소</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">변경 저장</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
